import { useContext, useEffect, useMemo } from 'react'
import { UIContext } from 'Context/UI'
import { TableContext } from 'Context/Table'
import { useNavigate } from 'react-router-dom'
import { useLang } from '_metronic/i18n/Metronici18n'
import { MOCK_DATA } from 'Data/mockMarketplaceCoupon'
import ActionCell from './Components/ActionCell'
import moment from 'moment'
import 'moment-timezone'
import Swal from 'sweetalert2'
import { toast } from 'react-toastify'
import clsx from 'clsx'
import formatNumberCommas from 'Utils/formatNumberCommas'
import formatNumberCommasNoDecimal from 'Utils/formatNumberCommasNoDecimal'

const ViewModel = () => {
	const lang = useLang()
	const navigate = useNavigate()
	const { setShowContentToolbar, setCustomContentToolbar } = useContext(UIContext)
	const { updatePagination, updateLoading, updateError, updateSorting } = useContext(TableContext)

	const totalData = MOCK_DATA.length
	const data = useMemo(() => MOCK_DATA, [])

	const setupTable = () => {
		updateSorting('title', true)
		updatePagination(true)
	}

	const setupUI = () => {
		setShowContentToolbar(true)
		setCustomContentToolbar(
			<button
				className="btn btn-sm btn-kumopack-primary-600 text-kumopack-base-white fw-bold"
				onClick={onCreate}>
				<img src="/media/icons/kumopack/plus-white.svg" alt="plus-white" />
				สร้างคูปอง
			</button>
		)
	}

	const resetUI = () => {
		setShowContentToolbar(false)
		setCustomContentToolbar(null)
	}

	const fetchData = () => {
		updateLoading(true)
		updateError(false)

		setTimeout(() => {
			updateLoading(false)
		}, 500)
	}

	const onCreate = () => {
		navigate('/marketplace/coupons/create')
	}

	const onEdit = (id: string) => {
		navigate(`/marketplace/coupons/${id}`)
	}

	const onDelete = (id: string) => {
		Swal.fire({
			title: lang === 'en' ? 'Delete Coupon' : 'ลบคูปอง',
			text:
				lang === 'en'
					? 'Are you sure you want to delete this coupon?'
					: 'คุณแน่ใจว่าต้องการลบคูปองนี้?',
			icon: 'warning',
			showCancelButton: true,
			cancelButtonText: lang === 'en' ? 'Cancel' : 'ยกเลิก',
			confirmButtonText: lang === 'en' ? 'Delete' : 'ลบ',
		}).then(result => {
			if (result.isConfirmed) {
				toast.success(lang === 'en' ? 'Coupon deleted successfully' : 'ลบคูปองสำเร็จ')
				fetchData()
			}
		})
	}

	const TABLE_CONFIGS: any[] = [
		{
			Header: 'รหัสคูปอง',
			accessor: 'code',
			minWidth: 100,
			width: 100,
			Cell: ({ value }: any) => <span className="text-kumopack-grey-600 fw-bold">{value}</span>,
		},
		{
			Header: 'ชื่อคูปอง',
			accessor: 'title',
			minWidth: 200,
			width: 200,
			Cell: ({ row }: any) => (
				<div
					className="d-flex align-items-center justify-content-left"
					style={{ columnGap: '8px' }}>
					<div className="rounded-circle overflow-hidden p-3 fw-bold bg-kumopack-grey-200 text-kumopack-grey-600">
						{row.original.title.substring(0, 3).toUpperCase()}
					</div>

					<div className="d-flex flex-column">
						<span className="text-kumopack-grey-900">{row.original.title}</span>
						<span className="text-kumopack-grey-600">Coupon Code : {row.original.code}</span>
					</div>
				</div>
			),
		},

		{
			Header: 'จำนวนการใช้คูปอง',
			accessor: 'usedAmount',
			width: 130,
			minWidth: 130,
			Cell: ({ row }: any) => (
				<div className="flex flex-row">
					<span className="text-kumopack-primary-600 fw-bolder">
						{formatNumberCommasNoDecimal(row.original.usedAmount)}
					</span>{' '}
					/{' '}
					<span className="text-kumopack-primary-700 fw-bolder">
						{formatNumberCommasNoDecimal(row.original.totalAmount)}
					</span>
				</div>
			),
		},
		{
			Header: 'ยอดสั่งซื้อทั้งหมด',
			accessor: 'order',
			width: 130,
			minWidth: 130,
			Cell: ({ value }: any) => (
				<div className="d-flex flex-column">
					<div className="text-kumopack-grey-900 fw-medium">฿{formatNumberCommas(value)}</div>
				</div>
			),
		},
		{
			Header: 'วันที่สามารถใช้งาน',
			accessor: 'startDate',
			minWidth: 200,
			width: 200,
			Cell: ({ row }: any) => {
				if (!row.original?.startDate || !row.original?.endDate)
					return <span className="text-kumopack-grey-600">-</span>

				const startDate = moment(row.original.startDate).format('DD/MM/YYYY')
				const endDate = moment(row.original.endDate).format('DD/MM/YYYY')

				return (
					<span className="text-kumopack-grey-600">
						{startDate} - {endDate}
					</span>
				)
			},
		},
		{
			Header: 'ประเภท',
			accessor: 'type',
			minWidth: 200,
			width: 200,
			Cell: ({ value }: any) => (
				<div
					className={clsx('btn btn-sm px-4 py-1 border-radius-16px', {
						'border border-kumopack-success-700 text-kumopack-success-700': value === 'fixed',
						'border border-kumopack-purple-1 text-kumopack-purple-1': value === 'percentage',
					})}>
					{value === 'fixed' ? 'Value Coupon' : 'Percentage %'}
				</div>
			),
		},
		{
			Header: '',
			accessor: 'action',
			minWidth: 200,
			width: 200,
			Cell: ({ row }: any) => (
				<ActionCell id={row.original?.uuId} onEdit={onEdit} onDelete={onDelete} />
			),
		},
	]

	useEffect(() => {
		setupTable()
		setupUI()

		return () => {
			resetUI()
		}
		// eslint-disable-next-line
	}, [])

	useEffect(() => {
		fetchData()
		// eslint-disable-next-line
	}, [])

	return {
		totalData,
		data,
		TABLE_CONFIGS,
	}
}

export default ViewModel
